import { NewsStatus, ToolbarOption } from '@common/clients/api';
import { type ContextData } from '@common/defaults/ContextData';
import { getThemeConfigByContext, type ThemeConfig, useThemeConfig } from '@common/hooks/useThemeConfig';

export interface ArtikelThemeConfig {
    excludedArticleBlocks: ToolbarOption[];
    defaultStatus: NewsStatus;
    prefillInitialAuthor: boolean;
    richNextEditorSupport: RichNextEditorSupport;
}

export const footballRelatedArticleBlocks = [
    ToolbarOption.INSERT_MATCH_BLOCK,
    ToolbarOption.INSERT_MATCH_DETAIL_SECTION,
    ToolbarOption.INSERT_MATCH_PREDICTION_BLOCK,
    ToolbarOption.INSERT_PLAYER,
    ToolbarOption.INSERT_PLAYER_GRADE,
    ToolbarOption.INSERT_POULE,
    ToolbarOption.INSERT_STANDING_BLOCK,
];

export const articleBlockOrder: ToolbarOption[] = [
    ToolbarOption.INSERT_GALLERY,
    ToolbarOption.INSERT_IMAGES,
    ToolbarOption.INSERT_MEDIA_BLOCK,
    ToolbarOption.INSERT_RELATED_BLOCK,
    ...footballRelatedArticleBlocks,
    ToolbarOption.INSERT_POLL,
    ToolbarOption.INSERT_LISTICLE_BLOCK,
    ToolbarOption.INSERT_PAYWALL,
    ToolbarOption.INSERT_QUOTE,
    ToolbarOption.INSERT_POST,
    ToolbarOption.INSERT_INSTAGRAM_POST,
    ToolbarOption.INSERT_TIK_TOK_BLOCK,
    ToolbarOption.INSERT_YOUTUBE_VIDEO,
    ToolbarOption.INSERT_IFRAME,
    ToolbarOption.INSERT_EXTERNAL_SNIPPET,
    ToolbarOption.INSERT_WATT_WIZARD_BLOCK,
    ToolbarOption.INSERT_ACCESS_WALL,
    ToolbarOption.INSERT_JOBS_OVERVIEW,
];

export enum RichNextEditorSupport {
    FULL,
    PARTIAL_HIDE_DEPRECATED_FIELDS,
    PARTIAL_SHOW_DEPRECATED_FIELDS,
}

export const THEME_CONFIG: ThemeConfig<ArtikelThemeConfig> = {
    defaults: {
        excludedArticleBlocks: [
            ...footballRelatedArticleBlocks,
            ToolbarOption.INSERT_PLAYER_GRADE,
            ToolbarOption.INSERT_WATT_WIZARD_BLOCK,
            ToolbarOption.INSERT_ACCESS_WALL,
        ],
        defaultStatus: NewsStatus.DRAFT,
        prefillInitialAuthor: true,
        richNextEditorSupport: RichNextEditorSupport.PARTIAL_HIDE_DEPRECATED_FIELDS,
    },
    byPlatform: {
        br: {
            excludedArticleBlocks: [
                ...footballRelatedArticleBlocks,
                ToolbarOption.INSERT_PLAYER_GRADE,
                ToolbarOption.INSERT_ACCESS_WALL,
            ],
        },
        rw: {
            excludedArticleBlocks: [
                ...footballRelatedArticleBlocks,
                ToolbarOption.INSERT_PLAYER_GRADE,
                ToolbarOption.INSERT_WATT_WIZARD_BLOCK,
            ],
        },
        vi: {
            excludedArticleBlocks: [ToolbarOption.INSERT_WATT_WIZARD_BLOCK, ToolbarOption.INSERT_ACCESS_WALL],
            richNextEditorSupport: RichNextEditorSupport.FULL,
            prefillInitialAuthor: false,
        },
        vp: {
            //FIXME: INSERT_JOBS_OVERVIEW is temporarily disabled. On small some important blocks where outside of bounds. Proper fix next week.
            excludedArticleBlocks: [
                ToolbarOption.INSERT_JOBS_OVERVIEW,
                ToolbarOption.INSERT_PAYWALL,
                ToolbarOption.INSERT_WATT_WIZARD_BLOCK,
            ],
            defaultStatus: NewsStatus.PUBLISHED,
            prefillInitialAuthor: false,
            richNextEditorSupport: RichNextEditorSupport.PARTIAL_SHOW_DEPRECATED_FIELDS,
        },
        vn: {
            excludedArticleBlocks: [
                ...footballRelatedArticleBlocks,
                ToolbarOption.INSERT_PAYWALL,
                ToolbarOption.INSERT_WATT_WIZARD_BLOCK,
                ToolbarOption.INSERT_ACCESS_WALL,
            ],
            defaultStatus: NewsStatus.PUBLISHED,
            prefillInitialAuthor: false,
            richNextEditorSupport: RichNextEditorSupport.PARTIAL_SHOW_DEPRECATED_FIELDS,
        },
    },
    byHost: {
        vpbe: {
            excludedArticleBlocks: [
                ToolbarOption.INSERT_JOBS_OVERVIEW,
                ToolbarOption.INSERT_PAYWALL,
                ToolbarOption.INSERT_PLAYER_GRADE,
                ToolbarOption.INSERT_WATT_WIZARD_BLOCK,
                ToolbarOption.INSERT_ACCESS_WALL,
            ],
        },
    },
} as const;

export const getArtikelThemeConfig = (contextData: ContextData) =>
    getThemeConfigByContext({ config: THEME_CONFIG, contextData });

export const useArtikelThemeConfig = () => useThemeConfig(THEME_CONFIG);
